import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MasterFlightRecord, formatDisplayDate, formatFrequencyWithUnderscores, getDisplayFrequency, getDisplayDate, getRouteString } from '../../../utils/types';
import { SectionHeader } from '../common/SectionHeader';
import { computeComparison } from './variantComparison';
import { useTimezone } from '../../../contexts/TimezoneContext';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

interface ItineraryFullscreenModalProps {
  open: boolean;
  variants: MasterFlightRecord[];
  currentVariantId: string;
  flightOwner: string;
  onVariantSelect: (variant: MasterFlightRecord) => void;
  onClose: () => void;
}

const STATUS_OPTIONS = ['All', 'ACTIVE', 'INACTIVE', 'PENDING', 'CANCELLED', 'SUSPENDED'] as const;
const COMPARE_MAX = 3;
const GLOBAL_VARIANT_AIRLINES = ['OD'];

type SortKey =
  | 'itineraryVarId' | 'seasonId' | 'serviceType' | 'scheduleStatus'
  | 'startDate' | 'endDate' | 'frequency' | 'opSuffix'
  | 'route' | 'sectors' | 'aircraft' | 'config' | 'onwardFlight';

interface ColumnDef {
  key: SortKey;
  label: string;
  extract: (v: MasterFlightRecord) => string;
  mono?: boolean;
}

function getColumns(tz: 'UTC' | 'MYT'): ColumnDef[] {
  return [
    { key: 'itineraryVarId', label: 'Var', extract: v => String(v.itineraryVarId) },
    { key: 'seasonId', label: 'Season', extract: v => v.seasonId || '—' },
    { key: 'serviceType', label: 'Service Type', extract: v => v.iataServiceTypeCode || '—' },
    { key: 'scheduleStatus', label: 'Status', extract: v => v.scheduleStatus || '—' },
    { key: 'startDate', label: 'Start Date', extract: v => formatDisplayDate(getDisplayDate(v.startDate, v.stations?.[0]?.std, tz)) },
    { key: 'endDate', label: 'End Date', extract: v => formatDisplayDate(getDisplayDate(v.endDate, v.stations?.[0]?.std, tz)) },
    { key: 'frequency', label: 'Frequency', extract: v => formatFrequencyWithUnderscores(getDisplayFrequency(v.frequency, v.frequencyMYT, tz)), mono: true },
    { key: 'opSuffix', label: 'Op. Suffix', extract: v => v.operationalSuffix || '—', mono: true },
    { key: 'route', label: 'Route', extract: v => getRouteString(v.stations) },
    { key: 'sectors', label: 'Sectors', extract: v => String(v.stations?.length || 0) },
    { key: 'aircraft', label: 'Aircraft', extract: v => v.stations?.[0]?.iataSubTypeCode || '—' },
    { key: 'config', label: 'Config', extract: v => v.stations?.[0]?.aircraftConfiguration || '—', mono: true },
    { key: 'onwardFlight', label: 'Onward Flight', extract: v => v.stations?.[0]?.onwardFlight || '—', mono: true },
  ];
}

export function ItineraryFullscreenModal({
  open,
  variants,
  currentVariantId,
  flightOwner,
  onVariantSelect,
  onClose,
}: ItineraryFullscreenModalProps) {
  const { timezone } = useTimezone();
  const columns = useMemo(() => getColumns(timezone), [timezone]);
  const extractSortValue = useCallback((v: MasterFlightRecord, key: SortKey): string | number => {
    switch (key) {
      case 'itineraryVarId': return v.itineraryVarId;
      case 'sectors': return v.stations?.length || 0;
      default: {
        const col = columns.find(c => c.key === key);
        return col ? col.extract(v) : '';
      }
    }
  }, [columns]);
  const isGlobalAirline = GLOBAL_VARIANT_AIRLINES.includes(flightOwner.toUpperCase());

  // --- Local state ---
  const [sortKey, setSortKey] = useState<SortKey>('itineraryVarId');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSeason, setSelectedSeason] = useState<string>('All');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [compareMode, setCompareMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showComparison, setShowComparison] = useState(false);

  // Reset all state when modal opens
  useEffect(() => {
    if (open) {
      setSortKey('itineraryVarId');
      setSortDir('asc');
      setSearchQuery('');
      setSelectedSeason('All');
      setStatusFilter('All');
      setCompareMode(false);
      setSelectedIds([]);
      setShowComparison(false);
    }
  }, [open]);

  // --- Derived data ---
  const availableSeasons = useMemo(() => {
    const seasons = Array.from(new Set(variants.map(v => v.seasonId)));
    return seasons.sort();
  }, [variants]);

  // Data pipeline: season filter → status filter → search filter → sort
  const processedVariants = useMemo(() => {
    let result = variants;

    // Season filter
    if (!isGlobalAirline && selectedSeason !== 'All') {
      result = result.filter(v => v.seasonId === selectedSeason);
    }

    // Status filter
    if (statusFilter !== 'All') {
      result = result.filter(v => (v.scheduleStatus || '').toUpperCase() === statusFilter);
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(v => {
        const searchable = [
          v.seasonId,
          v.scheduleStatus,
          v.iataServiceTypeCode,
          formatDisplayDate(v.startDate),
          formatDisplayDate(v.endDate),
          formatFrequencyWithUnderscores(v.frequency),
          v.operationalSuffix,
          getRouteString(v.stations),
          v.stations?.[0]?.iataSubTypeCode,
          v.stations?.[0]?.aircraftConfiguration,
          v.stations?.[0]?.onwardFlight,
          String(v.itineraryVarId),
        ].filter(Boolean).join(' ').toLowerCase();
        return searchable.includes(q);
      });
    }

    // Sort
    const sorted = [...result].sort((a, b) => {
      const aVal = extractSortValue(a, sortKey);
      const bVal = extractSortValue(b, sortKey);
      let cmp: number;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal));
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return sorted;
  }, [variants, isGlobalAirline, selectedSeason, statusFilter, searchQuery, sortKey, sortDir, extractSortValue]);

  // Season group dividers — only when sorted by varId or seasonId
  const seasonGroupInfo = useMemo(() => {
    const showDividers = sortKey === 'itineraryVarId' || sortKey === 'seasonId';
    if (!showDividers) return new Map<number, { seasonId: string; count: number }>();

    const map = new Map<number, { seasonId: string; count: number }>();
    let currentSeason: string | null = null;

    processedVariants.forEach((v, idx) => {
      if (v.seasonId !== currentSeason) {
        currentSeason = v.seasonId;
        let count = 0;
        for (let j = idx; j < processedVariants.length && processedVariants[j].seasonId === currentSeason; j++) {
          count++;
        }
        map.set(idx, { seasonId: v.seasonId, count });
      }
    });

    return map;
  }, [processedVariants, sortKey]);

  // Comparison data
  const comparisonData = useMemo(() => {
    if (selectedIds.length < 2) return null;
    const selected = selectedIds
      .map(id => variants.find(v => v._id === id))
      .filter((v): v is MasterFlightRecord => v != null);
    if (selected.length < 2) return null;
    return { variants: selected, ...computeComparison(selected, timezone) };
  }, [selectedIds, variants, timezone]);

  // --- Handlers ---
  const handleSort = useCallback((key: SortKey) => {
    setSortKey(prev => {
      if (prev === key) {
        setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        return key;
      }
      setSortDir('asc');
      return key;
    });
  }, []);

  const toggleCompareId = useCallback((id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : prev.length < COMPARE_MAX ? [...prev, id] : prev,
    );
  }, []);

  const handleRowClick = useCallback((variant: MasterFlightRecord) => {
    if (compareMode) {
      toggleCompareId(variant._id || '');
    } else {
      onVariantSelect(variant);
      onClose();
    }
  }, [compareMode, toggleCompareId, onVariantSelect, onClose]);

  const handleExitCompareMode = useCallback(() => {
    setCompareMode(false);
    setSelectedIds([]);
    setShowComparison(false);
  }, []);

  // Keyboard: layered Esc behavior
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (showComparison) {
          setShowComparison(false);
        } else if (compareMode) {
          handleExitCompareMode();
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, showComparison, compareMode, handleExitCompareMode, onClose]);

  if (!open) return null;

  const totalColumns = compareMode ? 14 : 13;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-5 z-[200] animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-h-[95vh] bg-white rounded-2xl shadow-2xl animate-slide-up fullscreen-modal flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 rounded-t-2xl border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-bold text-gray-900 m-0">
            Flight Itinerary ({variants.length})
          </h3>

          {/* Search input */}
          <div className="relative w-80">
            <input
              type="text"
              placeholder="Search variants..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm outline-none
                focus:border-primary-500 focus:ring-3 focus:ring-primary-500/10 transition-colors"
              aria-label="Search variants"
            />
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <SearchIcon sx={{ fontSize: 18 }} />
            </span>
          </div>

          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-200 text-gray-500 transition-colors"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* TOOLBAR */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Compare toggle */}
            {variants.length >= 2 && (
              <button
                onClick={() => {
                  if (compareMode) {
                    handleExitCompareMode();
                  } else {
                    setCompareMode(true);
                    setSelectedIds([]);
                    setShowComparison(false);
                  }
                }}
                className={`px-2.5 py-1 text-xs font-semibold rounded border transition-colors ${
                  compareMode
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {compareMode ? 'Exit Compare' : 'Compare'}
              </button>
            )}

            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="px-2.5 py-1 text-xs font-semibold rounded border border-gray-300 bg-white text-gray-700 cursor-pointer"
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt === 'All' ? 'All Statuses' : opt}</option>
              ))}
            </select>

            {/* Season filter pills */}
            {!isGlobalAirline && availableSeasons.length > 1 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-medium">Season:</span>
                <div className="flex gap-1">
                  {availableSeasons.map(season => (
                    <button
                      key={season}
                      onClick={() => setSelectedSeason(season)}
                      className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${
                        selectedSeason === season
                          ? 'bg-primary-500 text-white'
                          : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {season}
                    </button>
                  ))}
                  <button
                    onClick={() => setSelectedSeason('All')}
                    className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${
                      selectedSeason === 'All'
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    All
                  </button>
                </div>
              </div>
            )}
          </div>

          <span className="text-xs text-gray-500">
            Showing {processedVariants.length} of {variants.length} variants
          </span>
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* TABLE */}
          <div className={showComparison ? 'max-h-[45vh] overflow-y-auto' : ''}>
            <table className="w-full text-sm table-fixed">
              <thead className="bg-gray-100 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  {/* Checkbox column for compare mode */}
                  {compareMode && (
                    <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider w-10">
                      {/* checkbox header */}
                    </th>
                  )}
                  {columns.map(col => {
                    const active = sortKey === col.key;
                    return (
                      <th
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        className={`px-2 py-2 text-center cursor-pointer select-none whitespace-nowrap text-xs font-semibold uppercase tracking-wider
                          ${active ? 'text-primary-500' : 'text-gray-700'}`}
                      >
                        <span className="flex items-center gap-1 justify-center">
                          {col.label}
                          <span
                            className={`text-xs transition-transform ${active ? 'opacity-100' : 'opacity-30'} ${
                              sortDir === 'desc' && active ? 'rotate-180' : ''
                            }`}
                          >
                            ▲
                          </span>
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {processedVariants.length === 0 ? (
                  <tr>
                    <td colSpan={totalColumns} className="px-6 py-12 text-center text-sm text-gray-400">
                      No matching variants
                    </td>
                  </tr>
                ) : (
                  processedVariants.map((variant, idx) => {
                    const isCurrentVariant = variant._id === currentVariantId;
                    const isChecked = selectedIds.includes(variant._id || '');
                    const isDisabled = !isChecked && selectedIds.length >= COMPARE_MAX;
                    const groupInfo = seasonGroupInfo.get(idx);

                    return (
                      <React.Fragment key={variant._id}>
                        {/* Season group divider */}
                        {groupInfo && seasonGroupInfo.size > 1 && (
                          <tr>
                            <td
                              colSpan={totalColumns}
                              className="px-4 py-1.5 text-xs text-gray-400 font-medium tracking-wider border-t border-dashed border-gray-300 bg-gray-50"
                            >
                              <span className="inline-flex items-center gap-2">
                                <span className="h-px w-4 bg-gray-300" />
                                {groupInfo.seasonId} ({groupInfo.count} variant{groupInfo.count !== 1 ? 's' : ''})
                                <span className="h-px w-4 bg-gray-300" />
                              </span>
                            </td>
                          </tr>
                        )}

                        <tr
                          onClick={() => handleRowClick(variant)}
                          className={`cursor-pointer transition-colors ${
                            isCurrentVariant
                              ? 'bg-blue-50 hover:bg-blue-100'
                              : isChecked
                                ? 'bg-blue-50 hover:bg-blue-100'
                                : 'hover:bg-gray-50'
                          }`}
                        >
                          {/* Checkbox in compare mode */}
                          {compareMode && (
                            <td className="px-2 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isDisabled}
                                onChange={() => toggleCompareId(variant._id || '')}
                                onClick={e => e.stopPropagation()}
                                className="w-3.5 h-3.5 rounded border-gray-300 text-primary-500 focus:ring-primary-500 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                              />
                            </td>
                          )}

                          {/* Data columns */}
                          {columns.map(col => (
                            <td key={col.key} className="px-2 py-2 text-center">
                              <span className={`text-sm ${col.mono ? 'font-mono' : ''} ${
                                isCurrentVariant ? 'font-medium text-gray-900' : 'text-gray-600'
                              }`}>
                                {col.key === 'sectors' ? (
                                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold ${
                                    isCurrentVariant
                                      ? 'bg-blue-200 text-blue-800'
                                      : 'bg-gray-200 text-gray-700'
                                  }`}>
                                    {col.extract(variant)}
                                  </span>
                                ) : (
                                  col.extract(variant)
                                )}
                              </span>
                            </td>
                          ))}
                        </tr>
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* COMPARISON PANEL */}
          {showComparison && comparisonData && (
            <div className="max-h-[40vh] overflow-y-auto border-t border-gray-200">
              <div className="px-6 py-3">
                <SectionHeader title={`Comparison (${comparisonData.diffCount} difference${comparisonData.diffCount !== 1 ? 's' : ''})`} />
              </div>

              <div className="px-6 pb-4">
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 border-b border-gray-200 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-40">
                          Field
                        </th>
                        {comparisonData.variants.map(v => (
                          <th key={v._id} className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Var {v.itineraryVarId} ({v.seasonId})
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {comparisonData.rows.map(row => (
                        <tr
                          key={row.fieldKey}
                          className={row.hasDiff ? 'bg-amber-50' : ''}
                        >
                          <td className="px-3 py-1.5 text-xs font-medium text-gray-600 whitespace-nowrap">
                            <span className="flex items-center gap-1.5">
                              {row.hasDiff && (
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                              )}
                              {row.label}
                            </span>
                          </td>
                          {row.values.map((val, i) => (
                            <td
                              key={i}
                              className={`px-3 py-1.5 text-center text-xs ${row.mono ? 'font-mono' : ''} ${
                                row.hasDiff ? 'font-medium text-gray-900' : 'text-gray-600'
                              }`}
                            >
                              {val}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="mt-2 text-xs text-gray-400">
                  {comparisonData.diffCount} difference{comparisonData.diffCount !== 1 ? 's' : ''} found across {comparisonData.rows.length} fields
                </p>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex items-center justify-between px-6 py-3 bg-gray-50 rounded-b-2xl border-t border-gray-200 flex-shrink-0">
          <span className="text-xs text-gray-500">
            {compareMode
              ? showComparison
                ? `Comparing ${comparisonData?.variants.length || 0} itineraries`
                : `${selectedIds.length} of ${COMPARE_MAX} selected`
              : `${processedVariants.length} variant${processedVariants.length !== 1 ? 's' : ''}`
            }
          </span>

          <div className="flex items-center gap-2">
            {compareMode && showComparison ? (
              <>
                <button
                  onClick={() => setShowComparison(false)}
                  className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleExitCompareMode}
                  className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Exit Compare
                </button>
              </>
            ) : compareMode ? (
              <>
                <button
                  onClick={handleExitCompareMode}
                  className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={selectedIds.length < 2}
                  onClick={() => setShowComparison(true)}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Compare ({selectedIds.length})
                </button>
              </>
            ) : (
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
